// cSpell:ignore Silae SILAE rubrique
import type {
  SilaeFormData,
  SilaeBonusFormData,
  SilaeCondition,
  StructureType,
} from "@/types/silae";
export interface CodeMeta {
  name: string;
  description: string | null;
  tags: string[];
  createdAt?: string | null;
  updatedAt?: string | null;
}

export type GraphNodeKind =
  | "source"
  | "exclusions"
  | "conditions"
  | "bonusConditions"
  | "bonus"
  | "noBonus"
  | "branchGroup";

export type Branch = "if" | "else" | "main";

export interface GraphNodeData {
  [key: string]: unknown;
  kind: GraphNodeKind;
  label: string;
  branch?: Branch;
  conditions?: SilaeCondition[];
  conditionsOperators?: string[];
  bonus?: SilaeBonusFormData;
  bonusIndex?: number;
  targetStep?: number;
  /** Pour le node conditions : indique si une branche alternative (else) existe. */
  hasElseBranch?: boolean;
  /** Pour le node bonusConditions : libellé de la prime aval, injecté dans la phrase introductive. */
  bonusLabel?: string;
  /** Pour le node branchGroup : clé i18n du label flottant en haut-gauche. */
  branchGroupLabelKey?: string;
  /** Pour le node source : métadonnées du code (nom, description, tags). */
  codeMeta?: CodeMeta;
}

export interface GraphNode {
  id: string;
  data: GraphNodeData;
  width: number;
  height: number;
  /** Si défini, ce node est un enfant d'un sub-flow ReactFlow (groupe). */
  parentId?: string;
}

export interface GraphEdge {
  id: string;
  source: string;
  target: string;
  branch?: Branch;
}

export interface BuiltGraph {
  nodes: GraphNode[];
  edges: GraphEdge[];
}

import {
  NODE_DEFAULT_WIDTH,
  SOURCE_HEIGHT,
  PLACEHOLDER_HEIGHT,
  BONUS_BASE_HEIGHT,
  BONUS_TAUX_BLOCK_HEIGHT,
  CONDITIONS_BASE_HEIGHT,
  CONDITIONS_ROW_HEIGHT,
  MAX_VISIBLE_ROWS,
} from "./diagramConfig";

const NODE_WIDTH = NODE_DEFAULT_WIDTH;

const NODE_IDS = {
  source: "source-employee",
  exclusions: "exclusions",
  conditions: "conditions",
  withConditionsPlaceholder: "bonus-noop",
  emptyElsePlaceholder: "bonus-else-empty",
} as const;

const BRANCH_GROUP_LABEL_KEYS: Record<Branch, string> = {
  if: "step5Validation.branchGroup.ifLabel",
  else: "step5Validation.branchGroup.elseLabel",
  main: "step5Validation.branchGroup.mainLabel",
};

// Mapping vers les étapes du wizard (index 0-based stocké dans Redux).
// Step1 = structure + exclusions, Step2 = conditions, Step3 = IF, Step4 = ELSE.
const STEP = {
  structure: 0,
  conditions: 1,
  ifConfig: 2,
  elseConfig: 3,
} as const;

function bonusHeight(bonus: SilaeBonusFormData): number {
  // Les conditions propres au bonus sont désormais portées par un nœud
  // distinct en amont (`BonusConditionsNode`), donc elles n'entrent plus
  // dans la hauteur estimée du `BonusNode` lui-même.
  const tauxExtra =
    bonus.calculMethod === "1" || bonus.calculMethod === "3"
      ? BONUS_TAUX_BLOCK_HEIGHT
      : 0;
  return BONUS_BASE_HEIGHT + tauxExtra;
}

function conditionsHeight(count: number): number {
  return (
    CONDITIONS_BASE_HEIGHT +
    Math.min(count, MAX_VISIBLE_ROWS) * CONDITIONS_ROW_HEIGHT
  );
}

function ifBonusStep(structureType: StructureType): number {
  // Pour une structure "simple", il n'y a pas d'étape Conditions —
  // l'étape IF se trouve donc à l'index 1 (Step2) et non 2.
  return structureType === "simple" ? STEP.conditions : STEP.ifConfig;
}

class GraphBuilder {
  readonly nodes: GraphNode[] = [];
  readonly edges: GraphEdge[] = [];

  addNode(node: GraphNode): string {
    this.nodes.push(node);
    return node.id;
  }

  addEdge(source: string, target: string, branch?: Branch): void {
    this.edges.push({
      id: `e-${source}-${target}`,
      source,
      target,
      branch,
    });
  }

  build(): BuiltGraph {
    return { nodes: this.nodes, edges: this.edges };
  }
}

function makeSourceNode(codeMeta?: CodeMeta): GraphNode {
  const width = NODE_WIDTH;
  const charsPerLine = Math.floor(width / 7);
  let height = SOURCE_HEIGHT;
  if (codeMeta) {
    if (codeMeta.description) {
      const lines = Math.max(
        1,
        Math.ceil(codeMeta.description.length / charsPerLine),
      );
      height += lines * 16 + 8;
    }
    if (codeMeta.tags.length > 0) {
      height += 40;
    }
  }
  return {
    id: NODE_IDS.source,
    data: { kind: "source", label: "Employé", codeMeta },
    width,
    height,
  };
}

function makeExclusionsNode(values: SilaeFormData): GraphNode {
  return {
    id: NODE_IDS.exclusions,
    data: {
      kind: "exclusions",
      label: "Exclusions",
      conditions: values.exclusions,
      conditionsOperators: values.exclusionsOperators,
      targetStep: STEP.structure,
    },
    width: NODE_WIDTH,
    height: conditionsHeight(values.exclusions?.length ?? 0),
  };
}

function makeConditionsNode(values: SilaeFormData): GraphNode {
  return {
    id: NODE_IDS.conditions,
    data: {
      kind: "conditions",
      label: "Conditions",
      conditions: values.conditions,
      conditionsOperators: values.conditionsOperators,
      targetStep: STEP.conditions,
      hasElseBranch:
        values.structureType === "if_else" ||
        values.structureType === "with_conditions",
    },
    width: NODE_WIDTH,
    height: conditionsHeight(values.conditions?.length ?? 0),
  };
}

function makeBonusNode(
  bonus: SilaeBonusFormData,
  index: number,
  branch: Branch,
  targetStep: number,
): GraphNode {
  return {
    id: `bonus-${branch}-${index}`,
    data: {
      kind: "bonus",
      label: bonus.libelle,
      bonus,
      bonusIndex: index,
      branch,
      targetStep,
    },
    width: NODE_WIDTH,
    height: bonusHeight(bonus),
  };
}

function makeBonusConditionsNode(
  bonus: SilaeBonusFormData,
  index: number,
  branch: Branch,
  targetStep: number,
): GraphNode {
  const conds = bonus.conditions ?? [];
  return {
    id: `bonus-conditions-${branch}-${index}`,
    data: {
      kind: "bonusConditions",
      label: "Conditions",
      conditions: conds,
      conditionsOperators: bonus.conditionsOperators,
      bonusLabel: bonus.libelle,
      branch,
      targetStep,
    },
    width: NODE_WIDTH,
    height: conditionsHeight(conds.length),
  };
}

function hasBonusConditions(bonus: SilaeBonusFormData): boolean {
  return (
    bonus.hasConditions === true &&
    Array.isArray(bonus.conditions) &&
    bonus.conditions.length > 0
  );
}

function makeBranchGroupNode(branch: Branch, index: number): GraphNode {
  // Taille fictive : ELK la recalcule à partir des enfants. Les valeurs ici
  // servent juste d'estimation pour le tout premier rendu (avant que la
  // première passe ELK ne se termine).
  return {
    id: `branch-group-${branch}-${index}`,
    data: {
      kind: "branchGroup",
      label: "",
      branch,
      branchGroupLabelKey: BRANCH_GROUP_LABEL_KEYS[branch],
    },
    width: NODE_WIDTH + 40,
    height: 200,
  };
}

function makePlaceholderNode(id: string, label: string): GraphNode {
  return {
    id,
    data: { kind: "noBonus", label, branch: "else" },
    width: NODE_WIDTH,
    height: PLACEHOLDER_HEIGHT,
  };
}

function hasExclusions(values: SilaeFormData): boolean {
  return (
    values.hasExclusions === true &&
    Array.isArray(values.exclusions) &&
    values.exclusions.length > 0
  );
}

function hasMainConditions(values: SilaeFormData): boolean {
  const branching =
    values.structureType === "with_conditions" ||
    values.structureType === "if_else";
  return (
    branching &&
    Array.isArray(values.conditions) &&
    values.conditions.length > 0
  );
}

function withParent(node: GraphNode, parentId: string | undefined): GraphNode {
  return parentId ? { ...node, parentId } : node;
}

/**
 * Émet une prime (et éventuellement son nœud "Conditions de la prime" amont)
 * relié à `parentNodeId` via une arête `incomingBranch` (couleur de l'arête
 * entrante). `reactFlowParentId`, s'il est défini, place les nœuds en enfants
 * d'un sub-flow ReactFlow.
 */
function emitBonusWithOptionalConditions(args: {
  builder: GraphBuilder;
  parentNodeId: string;
  bonus: SilaeBonusFormData;
  index: number;
  branch: Branch;
  targetStep: number;
  incomingBranch: Branch;
  reactFlowParentId?: string;
}): void {
  const {
    builder,
    parentNodeId,
    bonus,
    index,
    branch,
    targetStep,
    incomingBranch,
    reactFlowParentId,
  } = args;
  const bonusNode = withParent(
    makeBonusNode(bonus, index, branch, targetStep),
    reactFlowParentId,
  );
  if (hasBonusConditions(bonus)) {
    // Insère un nœud "Conditions de la prime" en amont, relié au parent avec
    // la branche entrante, puis à la prime via une arête "Oui" verte.
    const conditionsId = builder.addNode(
      withParent(
        makeBonusConditionsNode(bonus, index, branch, targetStep),
        reactFlowParentId,
      ),
    );
    if (parentNodeId)
      builder.addEdge(parentNodeId, conditionsId, incomingBranch);
    builder.addNode(bonusNode);
    builder.addEdge(conditionsId, bonusNode.id, "if");
  } else {
    builder.addNode(bonusNode);
    if (parentNodeId)
      builder.addEdge(parentNodeId, bonusNode.id, incomingBranch);
  }
}

function appendBonusBranch(
  builder: GraphBuilder,
  parentId: string,
  bonuses: SilaeBonusFormData[],
  branch: Branch,
  targetStep: number,
): void {
  if (bonuses.length === 0) return;

  if (bonuses.length < 2) {
    bonuses.forEach((bonus, index) => {
      emitBonusWithOptionalConditions({
        builder,
        parentNodeId: parentId,
        bonus,
        index,
        branch,
        targetStep,
        incomingBranch: branch,
      });
    });
    return;
  }

  // ≥ 2 primes : on encadre la branche dans un sub-flow ReactFlow.
  // L'arête `parent → groupe` garde la couleur de la branche d'origine
  // (vert "Oui" / orange "Non" / gris). Les arêtes internes au groupe sont
  // neutres ("main") — le cadre porte déjà la sémantique.
  const groupId = builder.addNode(makeBranchGroupNode(branch, 0));
  if (parentId) builder.addEdge(parentId, groupId, branch);

  bonuses.forEach((bonus, index) => {
    emitBonusWithOptionalConditions({
      builder,
      parentNodeId: groupId,
      bonus,
      index,
      branch,
      targetStep,
      incomingBranch: "main",
      reactFlowParentId: groupId,
    });
  });
}

function appendElseBranch(
  builder: GraphBuilder,
  parentId: string,
  values: SilaeFormData,
): void {
  if (values.structureType === "if_else") {
    const elseBonuses = values.elseBranch?.bonuses ?? [];
    if (elseBonuses.length > 0) {
      appendBonusBranch(
        builder,
        parentId,
        elseBonuses,
        "else",
        STEP.elseConfig,
      );
      return;
    }
    const id = builder.addNode(
      makePlaceholderNode(NODE_IDS.emptyElsePlaceholder, "Aucune prime"),
    );
    builder.addEdge(parentId, id, "else");
    return;
  }

  const id = builder.addNode(
    makePlaceholderNode(NODE_IDS.withConditionsPlaceholder, "Rien"),
  );
  builder.addEdge(parentId, id, "else");
}

export function buildGraph(
  values: SilaeFormData,
  codeMeta?: CodeMeta,
): BuiltGraph {
  const builder = new GraphBuilder();
  let parentId = builder.addNode(makeSourceNode(codeMeta));

  if (hasExclusions(values)) {
    const exclusionsId = builder.addNode(makeExclusionsNode(values));
    if (parentId) builder.addEdge(parentId, exclusionsId);
    parentId = exclusionsId;
  }

  const ifStep = ifBonusStep(values.structureType);

  if (hasMainConditions(values)) {
    const conditionsId = builder.addNode(makeConditionsNode(values));
    if (parentId) builder.addEdge(parentId, conditionsId);

    appendBonusBranch(
      builder,
      conditionsId,
      values.ifBranch?.bonuses ?? [],
      "if",
      ifStep,
    );
    appendElseBranch(builder, conditionsId, values);
  } else {
    appendBonusBranch(
      builder,
      parentId,
      values.ifBranch?.bonuses ?? [],
      "main",
      ifStep,
    );
  }

  return builder.build();
}
